import React from 'react';
import { Card, Image, Box, IconButton, Icon, Text, Badge, HStack } from '@chakra-ui/react';
import {
  PencilIcon,
  TrashIcon,
  HeartIcon,
  DocumentDuplicateIcon,
  Bars3Icon,
  ShareIcon,
  EyeIcon,
  EyeSlashIcon,
  PrinterIcon,
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import { formatDistanceToNowStrict } from 'date-fns';
import { useParams } from 'react-router-dom';
import { Tooltip } from '@/components/ui/tooltip';
import { MenuContent, MenuItem, MenuRoot, MenuSeparator, MenuTrigger } from '@/components/ui/menu';
import { Content, Favorite, FieldOption } from '@/lib/types';
import useIsInAdminGroup from '@/lib/hooks/use-is-in-admin-group';
import useIsUser from '@/lib/hooks/use-is-user';
import { slotFor } from '@/lib/slot-color';

interface FilterConfig {
  label: string;
  id: string;
  key: string;
  options: FieldOption[];
}

type Pill = { label: string; slot: number };

function refIdFromFacetKey(key: string): string {
  return key.replace(/^content\./, '').replace(/\.value$/, '');
}

function getPillsForItem(item: Content, filterConfig: FilterConfig[] | undefined): Pill[] {
  if (!item.content || !filterConfig?.length) return [];
  const itemContent = item.content as Record<string, { value?: unknown } | undefined>;
  const pills: Pill[] = [];
  for (const f of filterConfig) {
    const refId = refIdFromFacetKey(f.key);
    const raw = itemContent[refId]?.value;
    if (raw == null || raw === '') continue;
    const values = Array.isArray(raw) ? raw : [raw];
    for (const v of values) {
      const label = f.options.find((o) => o.option_value === v)?.option_label;
      if (!label) continue;
      pills.push({ label, slot: slotFor(f.label) });
      if (pills.length >= 3) return pills;
    }
  }
  return pills;
}

function formatAge(createdAt: string | undefined): string {
  if (!createdAt) return '';
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return '';
  return `${formatDistanceToNowStrict(date, { addSuffix: false })} ago`
    .replace(' minutes', 'm')
    .replace(' minute', 'm')
    .replace(' hours', 'h')
    .replace(' hour', 'h')
    .replace(' days', 'd')
    .replace(' day', 'd')
    .replace(' months', 'mo')
    .replace(' month', 'mo')
    .replace(' years', 'y')
    .replace(' year', 'y');
}

type Props = {
  item: Content;
  favorites: { [key: number]: Favorite };
  viewMode: 'list' | 'grid';
  filterConfig?: FilterConfig[];
  lastContentElementRef: (node: HTMLDivElement | null) => void;
  onView: (item: Content) => void;
  onFavourite: (item: Content) => void;
  onGeneratePiblicLink: (item: Content) => void;
  onDuplicate: (item: Content) => void;
  onEdit: (item: Content) => void;
  onDelete: (item: Content) => void;
  onChangeStatus: (item: Content, status: 'draft' | 'published') => void;
  onPrint: (item: Content) => void;
};

const ItemCard: React.FC<Props> = ({
  item,
  favorites,
  viewMode,
  filterConfig,
  lastContentElementRef,
  onView,
  onFavourite,
  onGeneratePiblicLink,
  onDuplicate,
  onEdit,
  onDelete,
  onChangeStatus,
  onPrint,
}) => {
  const { workspaceId } = useParams<{
    workspaceId: string;
  }>();
  const isAdmin = useIsInAdminGroup();
  const isUser = useIsUser();
  const isListView = viewMode === 'list';
  const imageSrc = item.thumbnail ? item.thumbnail : '/no_media.png';
  const pills = isListView ? getPillsForItem(item, filterConfig) : [];

  function isFavourite(item: Content) {
    return favorites[item.id] ? true : false;
  }

  function actionPermission(itemWorkspaceId: number) {
    return (isAdmin || isUser) && workspaceId && itemWorkspaceId === parseInt(workspaceId, 10);
  }

  const isGrid = viewMode === 'grid';

  return (
    <Card.Root
      key={item.reference_id}
      ref={lastContentElementRef}
      borderWidth={isGrid ? 1 : 0}
      borderColor={isGrid ? 'border' : undefined}
      borderRadius={isGrid ? 'lg' : undefined}
      overflow={isGrid ? 'hidden' : undefined}
      height={isGrid ? '100%' : undefined}
      width={isGrid ? '100%' : undefined}
      display={isGrid ? 'flex' : undefined}
      flexDirection={isGrid ? 'column' : undefined}
      bg={isGrid ? 'bg.panel' : undefined}
      shadow={isGrid ? 'sm' : undefined}
      transition={isListView ? 'background 0.15s ease' : 'all 0.2s ease'}
      borderBottom={isListView ? '1px solid' : undefined}
      borderBottomColor={isListView ? 'border' : undefined}
      _hover={
        isGrid
          ? {
              transform: 'translateY(-2px)',
              shadow: 'md',
              borderColor: 'border.emphasized',
            }
          : { bg: 'bg.subtle' }
      }
    >
      <Box
        display={viewMode === 'list' ? 'flex' : 'block'}
        alignItems={'center'}
        flex={isGrid ? '1 1 auto' : undefined}
      >
        {isGrid && (
          <Image
            objectFit={'cover'}
            w='100%'
            h='130px'
            src={imageSrc}
            borderRadius={0}
            alt={item.name}
            loading='lazy'
            _hover={{ cursor: 'pointer' }}
            onClick={() => onView(item)}
          />
        )}
        <Card.Body p={2} _hover={{ cursor: 'pointer' }} onClick={() => onView(item)}>
          <Card.Title
            fontFamily='heading'
            fontWeight='700'
            fontSize='h3'
            color='fg.strong'
            lineHeight={1.4}
            lineClamp={isGrid ? 2 : undefined}
            minH={isGrid ? '2.8em' : undefined}
          >
            {item.name}
          </Card.Title>
          <Card.Description mt={1} color={'fg.muted'} fontSize={'xs'} lineClamp={1}>
            {formatAge(item.created_at) || ' '}
            {item.created_user && ` by ${item.created_user}`}
          </Card.Description>
          {isGrid ? (
            <Text mt={1} color={'fg.muted'} fontSize={'xs'} minH='1.2em' lineClamp={1}>
              {item.content_type_name ? (
                <Text as='span' color='brand.solid' fontWeight='medium'>
                  @{item.content_type_name}
                </Text>
              ) : (
                ' '
              )}
              {item.content_type_name && item.workspace && ' · '}
              {item.workspace && `@${item.workspace}`}
            </Text>
          ) : (
            <>
              {(item.content_type_name || item.workspace) && (
                <Text mt={1} color={'fg.muted'} fontSize={'xs'}>
                  {item.content_type_name && (
                    <Text as='span' color='brand.solid' fontWeight='medium'>
                      @{item.content_type_name}
                    </Text>
                  )}
                  {item.content_type_name && item.workspace && ' · '}
                  {item.workspace && `@${item.workspace}`}
                </Text>
              )}
              {pills.length > 0 && (
                <HStack gap={2} mt={2} flexWrap='wrap'>
                  {pills.map((pill, i) => (
                    <Badge
                      key={`${pill.label}-${i}`}
                      size='xs'
                      bg={`slot${pill.slot}.bg`}
                      color={`slot${pill.slot}.fg`}
                    >
                      {pill.label}
                    </Badge>
                  ))}
                </HStack>
              )}
            </>
          )}
        </Card.Body>
        <Card.Footer px={2} pb={2}>
          <Box display={viewMode === 'list' ? { base: 'none', md: 'flex' } : ''}>
            <Tooltip content={'Favourite'}>
              <IconButton
                variant={'ghost'}
                aria-label={'Favourite'}
                size={'sm'}
                onClick={() => onFavourite(item)}
              >
                <Icon
                  as={isFavourite(item) ? HeartSolidIcon : HeartIcon}
                  color={isFavourite(item) ? 'danger' : 'fg.muted'}
                />
              </IconButton>
            </Tooltip>
            <Tooltip content={'Print'}>
              <IconButton
                variant={'ghost'}
                aria-label={'Print'}
                size={'sm'}
                onClick={() => onPrint(item)}
              >
                <Icon as={PrinterIcon} />
              </IconButton>
            </Tooltip>
            {item.public_link && !actionPermission(item.workspace_id) && (
              <Tooltip content={'Share public link'}>
                <IconButton
                  variant={'ghost'}
                  aria-label={'Share public link'}
                  size={'sm'}
                  onClick={() => onGeneratePiblicLink(item)}
                >
                  <Icon as={ShareIcon} />
                </IconButton>
              </Tooltip>
            )}
            {actionPermission(item.workspace_id) && (
              <>
                <Tooltip
                  content={`${item.status === 'published' ? 'Hide from Workspace' : 'Share to Workspace'}`}
                >
                  <IconButton
                    variant={'ghost'}
                    aria-label={`${item.status}`}
                    size={'sm'}
                    onClick={() =>
                      onChangeStatus(item, item.status === 'published' ? 'draft' : 'published')
                    }
                  >
                    <Icon as={item.status === 'published' ? EyeIcon : EyeSlashIcon} />
                  </IconButton>
                </Tooltip>
                <Tooltip content={'Share public link'}>
                  <IconButton
                    variant={'ghost'}
                    aria-label={'Share public link'}
                    size={'sm'}
                    onClick={() => onGeneratePiblicLink(item)}
                  >
                    <Icon as={ShareIcon} />
                  </IconButton>
                </Tooltip>
                <Tooltip content={'Duplicate'}>
                  <IconButton
                    variant={'ghost'}
                    aria-label={'Duplicate'}
                    size={'sm'}
                    onClick={() => onDuplicate(item)}
                  >
                    <Icon as={DocumentDuplicateIcon} />
                  </IconButton>
                </Tooltip>
                <Tooltip content={'Edit'}>
                  <IconButton
                    variant={'ghost'}
                    aria-label={'Edit'}
                    size={'sm'}
                    onClick={() => onEdit(item)}
                  >
                    <Icon as={PencilIcon} />
                  </IconButton>
                </Tooltip>
                <Tooltip content={'Delete'}>
                  <IconButton
                    variant={'ghost'}
                    aria-label={'Delete'}
                    size={'sm'}
                    _hover={{ color: 'danger', bg: 'danger.subtle' }}
                    onClick={() => onDelete(item)}
                  >
                    <Icon as={TrashIcon} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
          {viewMode === 'list' && (
            <Box display={{ base: 'block', md: 'none' }}>
              <MenuRoot>
                <MenuTrigger asChild>
                  <IconButton variant='outline' aria-label='Menu' size='sm'>
                    <Icon as={Bars3Icon} />
                  </IconButton>
                </MenuTrigger>
                <MenuContent>
                  <MenuItem value={'favourite'} onClick={() => onFavourite(item)}>
                    <Icon
                      as={isFavourite(item) ? HeartSolidIcon : HeartIcon}
                      color={isFavourite(item) ? 'danger' : 'fg.muted'}
                      w={4}
                      h={4}
                    />
                    Favourite
                  </MenuItem>
                  {item.public_link && !actionPermission(item.workspace_id) && (
                    <MenuItem value='share-public-link' onClick={() => onGeneratePiblicLink(item)}>
                      <Icon as={ShareIcon} w={4} h={4} />
                      Share public link
                    </MenuItem>
                  )}
                  {actionPermission(item.workspace_id) && (
                    <>
                      <MenuItem
                        value={'change-status'}
                        onClick={() =>
                          onChangeStatus(item, item.status === 'published' ? 'draft' : 'published')
                        }
                      >
                        <Icon
                          as={item.status === 'published' ? EyeIcon : EyeSlashIcon}
                          w={4}
                          h={4}
                        />
                        {item.status === 'published' ? 'Hide from Workspace' : 'Share to Workspace'}
                      </MenuItem>
                      <MenuItem
                        value='share-public-link'
                        onClick={() => onGeneratePiblicLink(item)}
                      >
                        <Icon as={ShareIcon} w={4} h={4} />
                        Share public link
                      </MenuItem>
                      <MenuItem value='duplicate' onClick={() => onDuplicate(item)}>
                        <Icon as={DocumentDuplicateIcon} w={4} h={4} />
                        Duplicate
                      </MenuItem>
                      <MenuItem value='edit' onClick={() => onEdit(item)}>
                        <Icon as={PencilIcon} w={4} h={4} />
                        Edit
                      </MenuItem>
                    </>
                  )}
                  <MenuItem value='print' onClick={() => onPrint(item)}>
                    <Icon as={PrinterIcon} w={4} h={4} />
                    Print
                  </MenuItem>
                  {actionPermission(item.workspace_id) && (
                    <>
                      <MenuSeparator />
                      <MenuItem value='delete' onClick={() => onDelete(item)} color='danger'>
                        <Icon as={TrashIcon} w={4} h={4} />
                        Delete
                      </MenuItem>
                    </>
                  )}
                </MenuContent>
              </MenuRoot>
            </Box>
          )}
        </Card.Footer>
      </Box>
    </Card.Root>
  );
};

export default ItemCard;
